/**
 * Secure template engine for browser environments
 * Supports doT-like syntax: {{=expression}}
 *
 * SECURITY: This is a SAFE replacement for the vulnerable doT library.
 * It provides variable interpolation WITHOUT arbitrary code execution.
 * Uses safe property path resolution + recursive descent parser (NO eval/Function).
 *
 * Supported syntax:
 *   {{=d[0].fieldName}}                         - Access array element property
 *   {{=f[1].formattedValue}}                    - Access formatted data
 *   {{=d[0]['field-name']}}                     - Access with string key
 *   {{=d[0].age + d[1].age}}                    - Safe calculations (numeric only)
 *   {{=(d[0].cost * d[0].qty) / 2}}             - Complex arithmetic with precedence
 *   {{=round((d[0].total + d[1].total) / 2, 2)}} - Rounding (to 2 decimals)
 *   {{=floor(d[0].value)}}                      - Round down to integer
 *   {{=ceil(d[0].value)}}                       - Round up to integer
 *   {{=abs(d[0].difference)}}                   - Absolute value
 *
 * Safe mathematical functions (whitelist only):
 *   round(value, decimals) - Round to specified decimal places (decimals: 0-20)
 *   floor(value)          - Round down to integer
 *   ceil(value)           - Round up to integer
 *   abs(value)            - Absolute value
 *
 * NOT supported (by design for security):
 *   {{=d[0].method()}}         - Arbitrary function calls
 *   {{=5 + 10}}                - Raw calculations (must use property paths)
 *   {{=Math.random()}}         - Global object access
 *   {{=d[0].name + d[1].name}} - String concatenation (numeric only)
 */

interface TemplateData {
    d: any[];  // Raw data
    f: any[];  // Formatted data
}

/**
 * Regular expression patterns used for security validation
 */
const PROPERTY_PATH_PATTERN = /^([df])(\[\d+\])((?:\.\w+|\['[\w-]+'\]|\["[\w-]+"\])*)$/;
const PROPERTY_NAME_PATTERN = /^[\w-]+$/;
const BRACKET_NOTATION_PATTERN_SINGLE = /\['[^']+'\]/g;
const BRACKET_NOTATION_PATTERN_DOUBLE = /\["[^"]+"\]/g;
const PROPERTY_PATH_EXTRACT_PATTERN = /([df]\[\d+\](?:\.\w+|\['[\w-]+'\]|\["[\w-]+"\])*)/g;
const OPERATORS_IN_EXPRESSION_PATTERN = /[+\-*/%()]/;

/**
 * Security blacklist: dangerous operators that should not appear in property paths
 */
const DANGEROUS_OPERATORS = ['+', '*', '/', '%', '&', '|', '^', '~', '=', '<', '>'];

/**
 * Security blacklist: properties that could leak JavaScript environment information
 */
const BLOCKED_PROPERTIES = [
    'constructor',
    '__proto__',
    'prototype',
    '__defineGetter__',
    '__defineSetter__',
    '__lookupGetter__',
    '__lookupSetter__',
    'valueOf',
    'toString',
    'toLocaleString',
    'hasOwnProperty',
    'isPrototypeOf',
    'propertyIsEnumerable',
];

/**
 * Safely resolves a property path in the data object
 * Examples: "d[0].name", "f[1]['field']"
 *
 * @param path - Property path string (e.g., "d[0].name")
 * @param data - Data context with d and f properties
 * @returns Resolved value or undefined if path is invalid
 */
function safeResolve(path: string, data: TemplateData): any {
    try {
        // Remove whitespace
        path = path.trim();

        // Security: Block function calls (contains parentheses)
        if (path.includes('(') || path.includes(')')) {
            console.warn('Template security: Function calls are not allowed', {path, origin: 'safeResolve'});
            return undefined;
        }

        // Whitelist: only allow d[...] or f[...] patterns (must have array access)
        // This prevents: window.x, document.x, this.x, eval.x, Math.x, etc.
        if (!path.startsWith('d[') && !path.startsWith('f[')) {
            console.warn('Template security: Only d and f variables are allowed', {path, origin: 'safeResolve'});
            return undefined;
        }

        // Parse the path into tokens
        // Supports: d[0].field, d[0]['field-name'], f[1].nested.prop
        const tokens = path.match(PROPERTY_PATH_PATTERN);

        if (!tokens) {
            console.warn('Template security: Invalid path format', {path, origin: 'safeResolve'});
            return undefined;
        }

        const [, root, arrayAccess, propertyPath] = tokens;

        // Security: Block operators in property paths (after parsing valid brackets)
        // Remove bracketed strings first to avoid false positives on hyphens in property names
        const pathWithoutBrackets = propertyPath
            .replace(BRACKET_NOTATION_PATTERN_SINGLE, '')
            .replace(BRACKET_NOTATION_PATTERN_DOUBLE, '');
        for (const op of DANGEROUS_OPERATORS) {
            if (pathWithoutBrackets.includes(op)) {
                console.warn('Template security: Operators are not allowed', {path, operator: op, origin: 'safeResolve'});
                return undefined;
            }
        }

        // Start with root object (d or f)
        let value: any = root === 'd' ? data.d : data.f;

        // Apply array access: [0]
        const index = parseInt(arrayAccess.slice(1, -1), 10);
        if (!Array.isArray(value) || index < 0 || index >= value.length) {
            return undefined;
        }
        value = value[index];

        // Apply property path: .field1.field2 or ['field1']['field2']
        if (propertyPath) {
            // Split by dots, but handle bracket notation
            const segments = propertyPath
                .replace(/\['([^']+)'\]/g, '.$1')  // ['field'] -> .field
                .replace(/\["([^"]+)"\]/g, '.$1')  // ["field"] -> .field
                .split('.')
                .filter(s => s.length > 0);

            for (const segment of segments) {
                if (value === null || value === undefined) {
                    return undefined;
                }

                // Block dangerous properties
                if (BLOCKED_PROPERTIES.includes(segment)) {
                    console.warn('Template security: Blocked dangerous property', {path, property: segment, origin: 'safeResolve'});
                    return undefined;
                }

                // Only allow alphanumeric, underscore, and hyphen in property names
                if (!PROPERTY_NAME_PATTERN.test(segment)) {
                    console.warn('Template security: Invalid property name', {path, property: segment, origin: 'safeResolve'});
                    return undefined;
                }

                value = value[segment];
            }
        }

        return value;
    } catch (e) {
        console.error('Template path resolution error:', path, e);
        return undefined;
    }
}

/**
 * Tokenizes an arithmetic expression into numbers, operators, parentheses, and function names
 * @param expr - Arithmetic expression (e.g., "round(123.45, 2) + 67")
 * @returns Array of tokens
 */
function tokenizeArithmetic(expr: string): string[] {
    const tokens: string[] = [];
    let current = '';

    for (let i = 0; i < expr.length; i++) {
        const char = expr[i];

        if (char === ' ') {
            if (current) {
                tokens.push(current);
                current = '';
            }
            continue;
        }

        if ('+-*/%(),'.includes(char)) {
            if (current) {
                tokens.push(current);
                current = '';
            }
            tokens.push(char);
        } else if ((char >= '0' && char <= '9') || char === '.') {
            current += char;
        } else if ((char >= 'a' && char <= 'z') || (char >= 'A' && char <= 'Z')) {
            // Allow letters for function names
            current += char;
        } else {
            throw new Error(`Invalid character in arithmetic expression: ${char}`);
        }
    }

    if (current) {
        tokens.push(current);
    }

    return tokens;
}

/**
 * Evaluates arithmetic expression using recursive descent parser
 * NO eval() or Function() constructor - 100% safe arithmetic evaluation
 *
 * Grammar (with operator precedence and safe functions):
 *   Expression → Term (('+' | '-') Term)*
 *   Term → Factor (('*' | '/' | '%') Factor)*
 *   Factor → Number | '-' Factor | '+' Factor | '(' Expression ')' | Function
 *   Function → 'round' '(' Expression ',' Expression ')' |
 *              'floor' '(' Expression ')' |
 *              'ceil' '(' Expression ')' |
 *              'abs' '(' Expression ')'
 *
 * @param tokens - Tokenized arithmetic expression
 * @returns Evaluated result
 */
function evaluateArithmetic(tokens: string[]): number {
    let pos = 0;

    // Whitelist of allowed mathematical functions
    const SAFE_FUNCTIONS = ['round', 'floor', 'ceil', 'abs'];

    function parseExpression(): number {
        let left = parseTerm();

        while (pos < tokens.length && (tokens[pos] === '+' || tokens[pos] === '-')) {
            const op = tokens[pos++];
            const right = parseTerm();

            if (op === '+') {
                left = left + right;
            } else {
                left = left - right;
            }
        }

        return left;
    }

    function parseTerm(): number {
        let left = parseFactor();

        while (pos < tokens.length && (tokens[pos] === '*' || tokens[pos] === '/' || tokens[pos] === '%')) {
            const op = tokens[pos++];
            const right = parseFactor();

            if (op === '*') {
                left = left * right;
            } else if (op === '/') {
                if (right === 0) {
                    throw new Error('Division by zero');
                }
                left = left / right;
            } else {
                if (right === 0) {
                    throw new Error('Modulo by zero');
                }
                left = left % right;
            }
        }

        return left;
    }

    function parseFactor(): number {
        if (pos >= tokens.length) {
            throw new Error('Unexpected end of expression');
        }

        const token = tokens[pos];

        // Handle mathematical functions (whitelist only)
        if (SAFE_FUNCTIONS.includes(token.toLowerCase())) {
            return parseFunction();
        }

        // Handle parentheses
        if (token === '(') {
            pos++; // consume '('
            const result = parseExpression();
            if (pos >= tokens.length || tokens[pos] !== ')') {
                throw new Error('Mismatched parentheses');
            }
            pos++; // consume ')'
            return result;
        }

        // Handle unary minus
        if (token === '-') {
            pos++;
            return -parseFactor();
        }

        // Handle unary plus
        if (token === '+') {
            pos++;
            return parseFactor();
        }

        // Parse number
        const num = parseFloat(token);
        if (isNaN(num)) {
            throw new Error(`Invalid token: ${token}`);
        }
        pos++;
        return num;
    }

    function parseFunction(): number {
        const funcName = tokens[pos++].toLowerCase();

        // Expect opening parenthesis
        if (pos >= tokens.length || tokens[pos] !== '(') {
            throw new Error(`Expected '(' after function ${funcName}`);
        }
        pos++; // consume '('

        if (funcName === 'round') {
            // round(value, decimals)
            const value = parseExpression();

            if (pos >= tokens.length || tokens[pos] !== ',') {
                throw new Error('round() requires 2 arguments: round(value, decimals)');
            }
            pos++; // consume ','

            const decimals = parseExpression();

            if (pos >= tokens.length || tokens[pos] !== ')') {
                throw new Error('Expected closing parenthesis for round()');
            }
            pos++; // consume ')'

            // Validate decimals parameter
            if (!Number.isInteger(decimals)) {
                throw new Error('round() decimals must be an integer');
            }
            if (decimals < 0) {
                throw new Error('round() decimals must not be negative');
            }
            if (decimals > 20) {
                throw new Error('round() decimals must not exceed 20 (prevents Infinity from Math.pow)');
            }

            const multiplier = Math.pow(10, decimals);
            return Math.round(value * multiplier) / multiplier;

        } else if (funcName === 'floor') {
            // floor(value)
            const value = parseExpression();

            if (pos >= tokens.length || tokens[pos] !== ')') {
                throw new Error('Expected closing parenthesis for floor()');
            }
            pos++; // consume ')'

            return Math.floor(value);

        } else if (funcName === 'ceil') {
            // ceil(value)
            const value = parseExpression();

            if (pos >= tokens.length || tokens[pos] !== ')') {
                throw new Error('Expected closing parenthesis for ceil()');
            }
            pos++; // consume ')'

            return Math.ceil(value);

        } else if (funcName === 'abs') {
            // abs(value)
            const value = parseExpression();

            if (pos >= tokens.length || tokens[pos] !== ')') {
                throw new Error('Expected closing parenthesis for abs()');
            }
            pos++; // consume ')'

            return Math.abs(value);

        } else {
            throw new Error(`Unknown function: ${funcName}`);
        }
    }

    const result = parseExpression();

    // Ensure all tokens were consumed
    if (pos < tokens.length) {
        throw new Error(`Unexpected token: ${tokens[pos]}`);
    }

    return result;
}

/**
 * Safely evaluates a mathematical expression with property paths
 * Supports: d[0].field, f[1].field, +, -, *, /, %, (, )
 *
 * SECURITY: Uses recursive descent parser - NO eval() or Function() constructor
 * This eliminates ALL code injection risk - only safe arithmetic operations
 *
 * @param expression - Expression like "d[0].age + d[1].age" or "d[0].cost * 1.5"
 * @param data - Data context
 * @returns Evaluated result or undefined if invalid
 */
function safeEvaluateExpression(expression: string, data: TemplateData): any {
    try {
        expression = expression.trim();

        // Check if expression contains operators (calculation needed)
        // First, remove bracket notation to avoid false positives from hyphens in property names
        const expressionWithoutBrackets = expression
            .replace(BRACKET_NOTATION_PATTERN_SINGLE, '')
            .replace(BRACKET_NOTATION_PATTERN_DOUBLE, '');
        const hasOperators = OPERATORS_IN_EXPRESSION_PATTERN.test(expressionWithoutBrackets);

        if (!hasOperators) {
            // No operators - just resolve as property path
            return safeResolve(expression, data);
        }

        // SECURITY STEP 1: Extract all property paths (d[0].field, f[1].field)
        const propertyPaths = expression.match(PROPERTY_PATH_EXTRACT_PATTERN) || [];

        // SECURITY: If no property paths found, this is a raw calculation (5 + 10) which is blocked
        if (propertyPaths.length === 0) {
            console.warn('Template security: Calculations must include property paths (d[...] or f[...])', {expression, origin: 'safeEvaluateExpression'});
            return undefined;
        }

        // SECURITY STEP 2: Resolve property paths to their values
        const resolvedValues = new Map<string, number>();
        for (const path of propertyPaths) {
            const value = safeResolve(path, data);

            // Convert to number (if it's not a number, evaluation will fail safely)
            const numValue = typeof value === 'number' ? value : parseFloat(String(value));

            if (isNaN(numValue)) {
                console.warn('Template security: Non-numeric value in calculation', {expression, path, value, origin: 'safeEvaluateExpression'});
                return undefined;
            }

            resolvedValues.set(path, numValue);
        }

        // SECURITY STEP 3: Replace property paths with their numeric values
        let arithmeticExpression = expression;
        for (const [path, value] of resolvedValues.entries()) {
            // Escape regex special characters in the path
            const escapedPath = path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            arithmeticExpression = arithmeticExpression.replace(new RegExp(escapedPath, 'g'), String(value));
        }

        // SECURITY STEP 4: Tokenize and validate (only numbers, operators, parentheses)
        const tokens = tokenizeArithmetic(arithmeticExpression);

        // SECURITY STEP 5: Evaluate using recursive descent parser
        // NO eval() or Function() - completely safe arithmetic-only evaluation
        const result = evaluateArithmetic(tokens);

        return result;
    } catch (e) {
        console.error('Template expression evaluation error:', expression, e);
        return undefined;
    }
}

/**
 * Compiles a template string into a function that can render data
 * Supports doT syntax: {{=expression}}
 *
 * SECURITY: Uses safe property resolution for simple paths, safe expression evaluator for calculations
 *
 * XSS Protection: HTML escaping is handled by React when rendering the output.
 * This prevents double-escaping issues while maintaining security since React
 * automatically escapes all content rendered in JSX.
 *
 * ⚠️ CRITICAL SECURITY REQUIREMENT:
 * The output of this function MUST be rendered in React JSX as {text}, NOT with dangerouslySetInnerHTML.
 * React's automatic escaping is the ONLY XSS protection layer. If you render outside React or
 * use dangerouslySetInnerHTML, you WILL introduce an XSS vulnerability.
 *
 * Example (SAFE):
 *   const text = compiledTemplate(data);
 *   return <div>{text}</div>;  // ✅ React escapes automatically
 *
 * Example (VULNERABLE - DO NOT DO THIS):
 *   const text = compiledTemplate(data);
 *   return <div dangerouslySetInnerHTML={{__html: text}} />;  // ❌ XSS VULNERABILITY
 *
 * @param template - Template string with {{=expression}} placeholders
 * @returns Function that takes data and returns rendered string
 */
export function compileTemplate(template: string): (data: TemplateData) => string {
    return (data: TemplateData) => {
        try {
            // Replace {{=expression}} with safely resolved/evaluated values
            return template.replace(/\{\{=(.*?)\}\}/g, (match, expression) => {
                try {
                    // SECURITY: Use safe expression evaluator (handles both simple paths and calculations)
                    const result = safeEvaluateExpression(expression, data);

                    if (result === null || result === undefined) {
                        return '';
                    }

                    // Convert to string (React will handle HTML escaping during render)
                    const strResult = String(result);
                    return strResult;
                } catch (e) {
                    console.error('Template expression error:', expression, e);
                    return match; // Return original if resolution fails
                }
            });
        } catch (e) {
            console.error('Template rendering error:', e);
            throw e;
        }
    };
}
